import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, X } from 'lucide-react';
import { extractInvoiceFields } from '@/ocr/index.js';
import { purchaseFormActions, usePurchaseForm } from '@/state/forms/purchaseFormSlice.js';

const actions = purchaseFormActions();

const fieldMap = [
  { key: 'proveedor', label: 'Proveedor' },
  { key: 'cuit', label: 'CUIT' },
  { key: 'tipo', label: 'Tipo de comprobante' },
  { key: 'puntoVenta', label: 'Punto de venta' },
  { key: 'numero', label: 'Número' },
  { key: 'fecha', label: 'Fecha' },
  { key: 'cae', label: 'CAE' },
  { key: 'caeVto', label: 'Vencimiento CAE' },
  { key: 'neto', label: 'Neto' },
  { key: 'iva21', label: 'IVA 21%' },
  { key: 'iva105', label: 'IVA 10.5%' },
  { key: 'iva27', label: 'IVA 27%' },
  { key: 'exento', label: 'Exento' },
  { key: 'noGravado', label: 'No gravado' },
  { key: 'percepIva', label: 'Percepción IVA' },
  { key: 'percepIibb', label: 'Percepción IIBB' },
  { key: 'otros', label: 'Otros' },
  { key: 'total', label: 'Total' },
];

function formatValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
  }
  return value;
}

export default function OcrDrawer({ isOpen, onClose, files }) {
  const { supplier, comprobante, importes } = usePurchaseForm(state => ({
    supplier: state.supplier,
    comprobante: state.comprobante,
    importes: state.importes,
  }));

  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const controllerRef = useRef();

  useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setProgress(0);
      setLogs([]);
      setResult(null);
      setError(null);
      controllerRef.current?.abort();
      controllerRef.current = null;
      return;
    }
    if (!files?.length) {
      setError('Subí al menos un archivo para poder ejecutar el OCR.');
      setStatus('error');
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus('running');
    setLogs(prev => [...prev, 'Iniciando análisis con Tesseract.js...']);

    extractInvoiceFields(files[0], {
      locale: 'es-AR',
      signal: controller.signal,
      onProgress: message => {
        if (message.progress) {
          setProgress(Math.round(message.progress * 100));
        }
        if (message.status) {
          setLogs(prev => [...prev, message.status]);
        }
      },
    })
      .then(payload => {
        setStatus('done');
        setResult(payload);
        setLogs(prev => [...prev, 'OCR finalizado. Revisá los valores sugeridos.']);
      })
      .catch(err => {
        if (controller.signal.aborted) {
          setLogs(prev => [...prev, 'OCR cancelado.']);
          return;
        }
        setStatus('error');
        setError(err.message || 'No pudimos procesar el archivo.');
      });

    return () => {
      controller.abort();
    };
  }, [isOpen, files]);

  const diffs = useMemo(() => {
    if (!result?.fields) {
      return [];
    }
    const currentValues = {
      proveedor: supplier?.razon_social || '',
      cuit: supplier?.cuit || '',
      tipo: comprobante.tipo,
      puntoVenta: comprobante.puntoVenta,
      numero: comprobante.numero,
      fecha: comprobante.fecha,
      cae: comprobante.cae,
      caeVto: comprobante.caeVto,
      neto: importes.neto,
      iva21: importes.iva21,
      iva105: importes.iva105,
      iva27: importes.iva27,
      exento: importes.exento,
      noGravado: importes.noGravado,
      percepIva: importes.percepIva,
      percepIibb: importes.percepIibb,
      otros: importes.otros,
      total: importes.total,
    };

    return fieldMap
      .map(def => {
        const suggested = result.fields[def.key];
        if (suggested === undefined || suggested === null || suggested === '') {
          return null;
        }
        const current = currentValues[def.key];
        const isDifferent =
          typeof suggested === 'number' ? Number(current) !== Number(suggested) : (current || '').toString() !== suggested.toString();
        if (!isDifferent) {
          return null;
        }
        return {
          ...def,
          current,
          suggested,
          confidence: result.confidences?.[def.key] ?? 0.5,
        };
      })
      .filter(Boolean);
  }, [result, supplier, comprobante, importes]);

  const applyField = entry => {
    switch (entry.key) {
      case 'proveedor':
        actions.setSupplier({ id: null, razon_social: entry.suggested, cuit: result.fields.cuit || '' });
        break;
      case 'cuit':
        actions.setSupplier({ id: null, razon_social: result.fields.proveedor || '', cuit: entry.suggested });
        break;
      case 'tipo':
      case 'puntoVenta':
      case 'numero':
      case 'fecha':
      case 'cae':
      case 'caeVto':
        actions.setComprobanteField(entry.key, entry.suggested);
        break;
      case 'total':
        actions.setTotal(entry.suggested);
        break;
      default:
        actions.setImporte(entry.key, entry.suggested);
        break;
    }
  };

  const applyAll = () => {
    diffs.forEach(applyField);
    onClose?.();
  };

  return (
    <div
      className={`fixed inset-y-0 right-0 z-40 w-full max-w-xl transform bg-[rgba(var(--panel),0.95)] shadow-[0_-24px_48px_rgba(var(--shadow),0.22)] transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ocr-drawer-title"
    >
      <div className="flex h-full flex-col">
        <header className="flex items-start justify-between border-b border-[rgba(var(--border),0.4)] px-6 py-5">
          <div>
            <h2 id="ocr-drawer-title" className="text-lg font-semibold text-[rgb(var(--fg))]">
              Lectura OCR de comprobante
            </h2>
            <p className="text-sm text-[rgba(var(--muted),0.85)]">
              Revisá las sugerencias detectadas y aplicá las que necesites.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              controllerRef.current?.abort();
              onClose?.();
            }}
            className="rounded-xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.6)] p-2 text-[rgba(var(--muted),0.9)] transition hover:text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Cerrar panel OCR</span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {status === 'running' ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.55)] px-4 py-10 text-sm text-[rgba(var(--muted),0.85)]">
              <Loader2 className="h-6 w-6 animate-spin text-[rgb(var(--brand))]" aria-hidden="true" />
              <span>Procesando archivo con OCR...</span>
              <div className="w-full rounded-full bg-[rgba(var(--border),0.4)]">
                <div
                  className="rounded-full bg-[rgb(var(--brand))] py-1 text-xs font-semibold text-white"
                  style={{ width: `${Math.min(progress, 100)}%`, transition: 'width 0.3s ease' }}
                >
                  <span className="px-3">{progress}%</span>
                </div>
              </div>
            </div>
          ) : null}

          {status === 'error' ? (
            <div className="rounded-2xl border border-[rgba(var(--brand),0.4)] bg-[rgba(var(--brand),0.12)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">
              {error}
            </div>
          ) : null}

          {status === 'done' ? (
            <div className="space-y-4">
              {diffs.length ? (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Campos sugeridos</h3>
                    <button
                      type="button"
                      onClick={applyAll}
                      className="inline-flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-xs font-semibold text-white shadow-[0_10px_22px_rgba(var(--shadow),0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                    >
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      Aplicar todo
                    </button>
                  </div>
                  <ul className="space-y-3">
                    {diffs.map(entry => (
                      <li
                        key={entry.key}
                        className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.55)] px-4 py-3 shadow-[0_12px_24px_rgba(var(--shadow),0.08)]"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-[rgb(var(--fg))]">{entry.label}</p>
                            <p className="text-xs text-[rgba(var(--muted),0.8)]">Confianza {(entry.confidence * 100).toFixed(0)}%</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => applyField(entry)}
                            className="rounded-xl border border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.12)] px-3 py-1 text-xs font-semibold text-[rgb(var(--brand-700))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            Aplicar
                          </button>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                          <div className="rounded-xl bg-[rgba(var(--panel),0.6)] p-3">
                            <p className="font-semibold text-[rgb(var(--fg))]">Actual</p>
                            <p className="mt-1 text-[rgba(var(--muted),0.85)]">{formatValue(entry.current) || '—'}</p>
                          </div>
                          <div className="rounded-xl bg-[rgba(var(--panel),0.6)] p-3">
                            <p className="font-semibold text-[rgb(var(--brand))]">Detectado</p>
                            <p className="mt-1 text-[rgb(var(--fg))]">{formatValue(entry.suggested)}</p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.55)] px-4 py-4 text-sm text-[rgba(var(--muted),0.85)]">
                  No encontramos diferencias relevantes. Podés revisar el texto reconocido y cargar manualmente.
                </div>
              )}
              <details className="rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--panel),0.4)] p-4 text-xs text-[rgba(var(--muted),0.8)]">
                <summary className="cursor-pointer text-sm font-semibold text-[rgb(var(--fg))]">Registros del OCR</summary>
                <ul className="mt-2 space-y-2">
                  {logs.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </details>
            </div>
          ) : null}

          {status !== 'done' ? (
            <div className="mt-6 rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--panel),0.4)] p-4 text-xs text-[rgba(var(--muted),0.8)]">
              <p className="font-semibold text-[rgb(var(--fg))]">Registros</p>
              <ul className="mt-2 space-y-2">
                {logs.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
